import { useMaskito } from '@maskito/react';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { FormEventHandler, ReactNode, useState } from 'react';
import { Input, InputProps } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const numberMaskingOptions = maskitoNumberOptionsGenerator({
  thousandSeparator: '.',
  precision: 2,
  decimalSeparator: ',',
  min: 0,
});

export function FloatNumberInput(
  props: InputProps & {
    onInput?: FormEventHandler<HTMLInputElement>;
    unit?: ReactNode;
    tooltipMessage?: string;
  },
) {
  const inputRef = useMaskito({ options: numberMaskingOptions });
  const [focus, setFocus] = useState(false);
  const { onInput, onBlur = undefined, className, ...otherProps } = props;

  return (
    <div className="flex items-center relative">
      <div className="grow">
        <Input
          onFocus={() => {
            setFocus(true);
          }}
          {...otherProps}
          className={`min-w-full grow ${className}`}
          ref={inputRef}
          onInput={onInput}
          onBlur={(e) => {
            setFocus(false);
            onBlur?.(e);
          }}
        />
      </div>
      {props.unit && (
        <Unit
          focus={focus}
          unit={props.unit}
          tooltipMessage={props.tooltipMessage}
        />
      )}
    </div>
  );
}

function Unit({
  tooltipMessage,
  unit,
  focus,
}: {
  unit: ReactNode;
  tooltipMessage?: string;
  focus: boolean;
}) {
  const TextElement = (
    <span
      className={`${focus ? 'text-primary-blue-B600' : 'text-neutrals-N700'} ${
        tooltipMessage ? 'cursor-help' : 'cursor-default'
      } font-extralight mr-4 absolute right-0`}>
      {unit}
    </span>
  );

  return tooltipMessage ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{TextElement}</TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          {tooltipMessage}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    TextElement
  );
}
